import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader, Message, Modal } from 'semantic-ui-react';
import moment from 'moment-timezone';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import UserContext from '../../../contexts/UserContext';
import * as CheckIn from '../../services/CheckIn';
import { CheckInPayload } from '../../types/CheckIn';
import { fetchFacilityDetails } from '../../services/Facilities';
import { listFeed } from '../../services/Feed';
import { FeedItem } from '../../types/FeedItem';
import { getFamilyList, updateRelative, updateResident } from '../../../services/family';
import { sendInvite, listResidentsInvites } from '../../../services/Invite';
import { Relative, Invitation } from '../../types/Relative';
import sendToast from '../../../utils/Toast';
import settleAll from '../../../utils/settleAll';
import { isValidEmailAddress } from '../../../utils/emailValidation';
import {
    fetchPaymentsPublicConfig,
    fetchResidentPaymentMethods,
    getMembershipSubscription,
    isMembershipSubscriptionNotFound,
} from '../../services/Payments';
import { PaymentPublicConfig, MembershipSubscription, ResidentPaymentMethod } from '../../types/Payments';
import {
    isGreetingsAddonActive,
    isGreetingsAddonSubscription,
    clearGreetingsSummaryCache,
} from '../../hooks/useGreetingsSummary';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import { invalidateResidentOrderCardEligibility, resolveHasDefaultCard } from '../../hooks/useResidentOrderCardEligibility';
import { getCheckInOutcome, CheckInOutcomeResult } from '../../utils/checkInGreeting';
import GreetingsAddonCard from './GreetingsAddonCard';
import { getResidentDisplayFirstName } from '../../utils/residentIdentity';
import './style.less';

/*
 * Neighbors Greetings page — built from Jillian's mockup (speak2-greetings.html).
 *
 * Enhancement 4 (paid add-on enrolment gating): a resident only sees the full
 * Greetings experience while they hold an active `greetingsAddon` membership
 * subscription. Without one, the page renders GreetingsAddonCard's "enroll"
 * state instead (see the early returns below); with one, a compact
 * GreetingsAddonCard "manage" row is shown near the top of the normal page.
 */

type ContactSort = 'first' | 'last' | 'added' | 'status';
type FullHistorySort = 'newest' | 'oldest' | 'most';

interface GreetingContact {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    relationship: string;
    greetingRecipient: boolean;
    seq: number;
}

// A feed item's DateAdded: an ISO string, a numeric string, or seconds / milliseconds.
type HistoryDateValue = string | number | null | undefined;

interface HistoryRow {
    id: string;
    date: HistoryDateValue;
    message: string;
    // True recipient count from the feed item. `recipients` only holds the names
    // the backend had on file, which can be fewer (blank names are filtered out).
    count: number;
    recipients: string[];
    // Contacts the greeting could not reach (the rest got it); 0 when none failed.
    failed: number;
}

const Svg: React.FC<{ s: number; sw: number; className?: string; children: React.ReactNode }> = ({
    s,
    sw,
    className,
    children,
}) => (
    <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {children}
    </svg>
);

const initials = (name: string): string =>
    name
        .split(' ')
        .map((p) => p[0] || '')
        .join('')
        .slice(0, 2)
        .toUpperCase();

// Mirrors AddressBookTab's getRelationshipLabel fallback (Relationship -> Manager
// -> staff-entered -> generic), so a contact with no relationship set reads the
// same way across the app instead of showing nothing.
const getRelationshipLabel = (relative: Relative): string => {
    if (relative.Relationship) return relative.Relationship;
    if (relative.Manager) return 'Relationship Manager';
    if (relative.isStaffEntered) return 'Staff entered contact';
    return 'Relationship';
};

const nameParts = (fullName: string): { first: string; last: string } => {
    const parts = fullName.trim().split(/\s+/);
    return {
        first: parts[0] || '',
        last: parts.length > 1 ? parts[parts.length - 1] : '',
    };
};

// Reads a feed date the way FeedItemCard does: numbers and numeric strings may be seconds or
// milliseconds, and a string without an offset is read in the facility time zone.
const parseHistoryDate = (value: HistoryDateValue, timezone?: string): moment.Moment => {
    if (value === null || value === undefined || value === '') return moment.invalid();
    const inZone = (v: string | number) => (timezone ? moment.tz(v, timezone) : moment(v));
    const toMillis = (n: number) => (n < 1e12 ? n * 1000 : n);
    if (typeof value === 'number') {
        return Number.isFinite(value) ? inZone(toMillis(value)) : moment.invalid();
    }
    const raw = String(value).trim();
    if (!raw) return moment.invalid();
    const parsed = timezone ? moment.tz(raw, moment.ISO_8601, true, timezone) : moment(raw, moment.ISO_8601, true);
    if (parsed.isValid()) return parsed;
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? inZone(toMillis(numeric)) : moment.invalid();
};

const historyTime = (value: HistoryDateValue): number => {
    const parsed = parseHistoryDate(value);
    return parsed.isValid() ? parsed.valueOf() : 0;
};

const formatHistoryDate = (value: HistoryDateValue, timezone?: string): string => {
    const parsed = parseHistoryDate(value, timezone);
    if (!parsed.isValid()) return 'Unknown time';
    return `${parsed.format('MMM D')} \u00b7 ${parsed.format('h:mm A')}`;
};

const toHistoryRows = (items: FeedItem[]): HistoryRow[] =>
    items.map((item: FeedItem) => ({
        id: item._id,
        date: item.DateAdded,
        message: item.GreetingMessage || '',
        count: typeof item.GreetingRecipientCount === 'number'
            ? item.GreetingRecipientCount
            : (item.GreetingRecipientNames?.length || 0),
        recipients: item.GreetingRecipientNames || [],
        failed: typeof item.GreetingFailedCount === 'number' && item.GreetingFailedCount > 0
            ? item.GreetingFailedCount
            : 0,
    }));

// A greeting that lands between two pages can come back on both: keep the first copy of each id.
const mergeHistoryRows = (existing: HistoryRow[], incoming: HistoryRow[]): HistoryRow[] => {
    const seen = new Set(existing.map((row) => row.id));
    const merged = [...existing];
    incoming.forEach((row) => {
        if (seen.has(row.id)) return;
        seen.add(row.id);
        merged.push(row);
    });
    return merged;
};

// Normalize a typed phone number to E.164 (a bare number is read as a US number), or '' when it is not a real number.
const toE164 = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed || !isValidPhoneNumber(trimmed, 'US')) return '';
    try {
        return parsePhoneNumber(trimmed, 'US')?.number || '';
    } catch (_error) {
        return '';
    }
};

// All Greeting History starts here: a 13 digit start date from before the product existed.
// (fromDate 0 is not a valid start for the feed query, so the window came back empty.)
const FULL_HISTORY_FROM = Date.UTC(2020, 0, 1);
// The history window ends this far ahead of the device clock: a device whose clock is behind the server
// would otherwise leave out a greeting the server has just written. Nothing is in the future.
const HISTORY_CLOCK_SKEW_MS = 24 * 60 * 60 * 1000;

const MESSAGE_MAX_LENGTH = 160;

const PAGE_SUB =
    'Your Hello! button always checks you in with the community. Daily Greetings adds a message to your family on top of that.';
const DEFAULT_INVITE_MESSAGE = 'You have been invited to join Speak2 Family!';

const NeighborsGreetings: React.FC = () => {
    const { userObject, setUser } = useContext(UserContext);
    const location = useLocation();

    const listOfResidents = (userObject && userObject.registrants && userObject.registrants.list) || [];
    const activeRegistrantsIndexes =
        (userObject && userObject.registrants && userObject.registrants.activeRegistrantsIndexes) || [];
    const activeResident = listOfResidents[activeRegistrantsIndexes[0]];

    // Only Edit Message is hidden from non-managers here. The other actions (contact toggles,
    // invites, subscription) are enforced server-side.
    const allowEdit = Boolean(activeResident?.manager);

    const residentId = activeResident?._id;
    const facilityId = activeResident?.Facility;

    // The same "does this community take card payments" answer the Home tile and the side menu use.
    const {
        facilityPaymentsEnabled,
        facilityLookupFailed,
        loading: paymentAccessLoading,
        retry: retryPaymentAccess,
    } = useNeighborsPaymentAccess();

    // ── Daily Greetings billing (Enh 4 paid add-on) ──
    const [greetingsSubscription, setGreetingsSubscription] = useState<MembershipSubscription | null>(null);
    const [greetingsAddonLoading, setGreetingsAddonLoading] = useState<boolean>(true);
    // The subscription lookup itself failed (as opposed to "never signed up"): show a retry, never the sign-up card.
    const [greetingsLookupFailed, setGreetingsLookupFailed] = useState<boolean>(false);
    const [greetingsReloadCount, setGreetingsReloadCount] = useState<number>(0);
    const [greetingsPublicConfig, setGreetingsPublicConfig] = useState<PaymentPublicConfig | null>(null);
    const [hasDefaultPaymentMethod, setHasDefaultPaymentMethod] = useState<boolean>(false);
    // The saved-cards lookup failed: Sign Up asks to try again instead of assuming there is no card.
    const [cardsLookupFailed, setCardsLookupFailed] = useState<boolean>(false);
    const greetingsStripePromise = useMemo<Promise<Stripe | null> | null>(() => {
        const publishableKey = greetingsPublicConfig?.publishableKey;
        if (!publishableKey) return null;
        return loadStripe(publishableKey);
    }, [greetingsPublicConfig?.publishableKey]);

    // The entitlement rule lives in isGreetingsAddonActive, shared with the home tile.
    const greetingsAddonActive = isGreetingsAddonActive(greetingsSubscription);
    const greetingsCancelAtPeriodEnd = greetingsAddonActive && greetingsSubscription?.cancelAtPeriodEnd === true;
    // The Home tile keeps its last Greetings answer (including the contact count and avatars) in the
    // browser. Drop it whenever something it shows changes here, so Home never draws the old one first.
    const forgetHomeGreetingsAnswer = () =>
        clearGreetingsSummaryCache(residentId ? String(residentId) : undefined, facilityId ? String(facilityId) : undefined);
    const greetingsEndLabel = greetingsSubscription?.currentPeriodEnd
        ? new Date(greetingsSubscription.currentPeriodEnd).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '';

    // ── Facility timezone + Hello!/check-in ──
    const [facilityTimeZone, setFacilityTimeZone] = useState<string>('');
    const [checkInStatus, setCheckInStatus] = useState<boolean>(false);
    const [residentCheckInStatusObj, setResidentCheckInStatusObj] = useState<CheckInPayload>();
    const [isCheckInStatusLoading, setIsCheckInStatusLoading] = useState<boolean>(false);
    const [isFetchingStatus, setIsFetchingStatus] = useState<boolean>(false);
    const [showCheckInConfirm, setShowCheckInConfirm] = useState<boolean>(false);
    // Bumped by the unenrolled "Turn on" pill; GreetingsAddonCard opens its sign-up flow on each change.
    const [signupRequest, setSignupRequest] = useState<number>(0);
    const [helloFeedback, setHelloFeedback] = useState<{ tone: 'success' | 'warn'; text: React.ReactNode } | null>(null);

    // ── My Greeting Message ──
    const [greetingMessage, setGreetingMessage] = useState<string>('');
    const [isEditingMessage, setIsEditingMessage] = useState<boolean>(false);
    const [messageDraft, setMessageDraft] = useState<string>('');
    const [isSavingMessage, setIsSavingMessage] = useState<boolean>(false);

    // ── My Greeting Contacts ──
    const [contacts, setContacts] = useState<GreetingContact[]>([]);
    const [contactsLoading, setContactsLoading] = useState<boolean>(true);
    const [contactSearch, setContactSearch] = useState<string>('');
    const [contactSort, setContactSort] = useState<ContactSort>('first');
    const [receivingOnly, setReceivingOnly] = useState<boolean>(false);
    const [togglingId, setTogglingId] = useState<string>('');
    const [showScrollCue, setShowScrollCue] = useState<boolean>(false);

    // ── Invite a Contact ──
    const [availableInvites, setAvailableInvites] = useState<number>(0);
    const [familyListLoadFailed, setFamilyListLoadFailed] = useState<boolean>(false);
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [inviteFormOpen, setInviteFormOpen] = useState<boolean>(false);
    const [inviteName, setInviteName] = useState<string>('');
    const [invitePhone, setInvitePhone] = useState<string>('');
    const [inviteEmail, setInviteEmail] = useState<string>('');
    const [inviteGreetingOn, setInviteGreetingOn] = useState<boolean>(true);
    const [isSendingInvite, setIsSendingInvite] = useState<boolean>(false);

    // ── Greeting History ──
    const [history, setHistory] = useState<HistoryRow[]>([]);
    const [historyLoading, setHistoryLoading] = useState<boolean>(true);
    // A failed load keeps the rows already on screen and offers a retry; it never reads as "No greetings sent yet".
    const [historyLoadFailed, setHistoryLoadFailed] = useState<boolean>(false);
    const [expandedHistory, setExpandedHistory] = useState<Record<string, boolean>>({});
    const [fullHistoryOpen, setFullHistoryOpen] = useState<boolean>(false);
    const [fullHistorySort, setFullHistorySort] = useState<FullHistorySort>('newest');
    // "All Greeting History" shows every greeting ever sent, not just the homepage
    // preview's 30-day/20-row window -- so it gets its own fetch + pagination
    // instead of reusing `history`.
    const [fullHistoryLoadFailed, setFullHistoryLoadFailed] = useState<boolean>(false);
    const [fullHistoryRows, setFullHistoryRows] = useState<HistoryRow[]>([]);
    const [fullHistoryLoading, setFullHistoryLoading] = useState<boolean>(false);
    const [fullHistoryLoadingMore, setFullHistoryLoadingMore] = useState<boolean>(false);
    const [fullHistoryPage, setFullHistoryPage] = useState<number>(1);
    const [fullHistoryTotalCount, setFullHistoryTotalCount] = useState<number>(0);

    const contactsListRef = useRef<HTMLDivElement | null>(null);
    const contactsSectionRef = useRef<HTMLDivElement | null>(null);
    const historyRetryRef = useRef<number | null>(null);
    // The installed Semantic UI React modal has no dialog role or focus handling of its own, so the
    // All Greeting History window moves focus in and back out itself.
    const historyViewAllRef = useRef<HTMLButtonElement | null>(null);
    const fullHistoryCloseRef = useRef<HTMLButtonElement | null>(null);
    // Each loader only applies the answer of its latest call, so switching resident (or closing the
    // history window) while a request is in flight can never paint the previous resident's data.
    const contactsRequestRef = useRef<number>(0);
    const historyRequestRef = useRef<number>(0);
    const fullHistoryRequestRef = useRef<number>(0);

    // ── Loaders ──
    // `isStale` lets the status effect drop an answer that arrives after another resident was selected.
    // `quiet` keeps a failed read from showing its own error toast, for a caller that reports it itself.
    const fetchCheckInStatus = async (tz: string, isStale?: () => boolean, quiet = false): Promise<boolean> => {
        try {
            const response = await CheckIn.fetchCurrentCheckInStatusOfResident(
                activeResident.Facility,
                String(activeResident._id),
                tz,
            );
            if (isStale && isStale()) return false;
            // Mirrors neighbors-home: the service returns Result.Content, and the
            // home page then reads Result[0] off that value. Kept identical for
            // behaviour parity.
            const result = (response as any)?.Result?.[0];
            setResidentCheckInStatusObj(result);
            let isCheckedIn = false;
            if (result?.column && result.column !== 'other' && result.column !== 'not_checked_in') {
                isCheckedIn = true;
            }
            setCheckInStatus(isCheckedIn);
            return isCheckedIn;
        } catch (error) {
            if (isStale && isStale()) return false;
            if (!quiet) sendToast('error', error instanceof Error ? error.message : 'Error while checking the check-in status');
            return false;
        }
    };

    const loadContacts = () => {
        if (!residentId) {
            setContactsLoading(false);
            return;
        }
        const requestId = contactsRequestRef.current + 1;
        contactsRequestRef.current = requestId;
        const isCurrent = () => contactsRequestRef.current === requestId;
        setContactsLoading(true);
        settleAll([getFamilyList(residentId), listResidentsInvites(residentId)])
            .then(([familyResult, inviteResult]) => {
                if (!isCurrent()) return;
                if (familyResult.status === 'fulfilled') {
                    const data = familyResult.value as { relatives: Relative[]; availableInvites: number };
                    // Mirror the backend greeting-delivery filter (GetRegistrantRelatives in
                    // live-backend excludes managerType "Resident"). The family list also
                    // returns the resident's own self-management row, which can never
                    // receive a greeting, so keep it out of the picker rather than showing
                    // it as a toggleable contact. Every other row from this endpoint is a
                    // real linked relative account, so no further id check is needed.
                    const active = (data.relatives || []).filter(
                        (r) => r.isActive === true && r.managerType !== 'Resident',
                    );
                    setContacts(
                        active.map((r, i) => ({
                            id: r._id,
                            name: `${r.FirstName || ''} ${r.LastName || ''}`.trim() || 'Contact',
                            firstName: r.FirstName || '',
                            lastName: r.LastName || '',
                            relationship: getRelationshipLabel(r),
                            greetingRecipient: r.greetingRecipient === true,
                            seq: i,
                        })),
                    );
                    setAvailableInvites(typeof data.availableInvites === 'number' ? data.availableInvites : 0);
                    setFamilyListLoadFailed(false);
                } else {
                    setFamilyListLoadFailed(true);
                    sendToast(
                        'error',
                        familyResult.reason instanceof Error ? familyResult.reason.message : 'Error loading contacts',
                    );
                }
                if (inviteResult.status === 'fulfilled') {
                    setInvitations([...(inviteResult.value || [])]);
                } else {
                    sendToast(
                        'error',
                        inviteResult.reason instanceof Error ? inviteResult.reason.message : 'Error loading invites',
                    );
                }
            })
            .catch((err) => {
                if (!isCurrent()) return;
                setFamilyListLoadFailed(true);
                sendToast('error', err instanceof Error ? err.message : 'Error loading contacts');
            })
            .finally(() => {
                if (isCurrent()) setContactsLoading(false);
            });
    };

    // `quiet` skips the loading state so a background refresh (e.g. the post-check-in
    // retry) doesn't flash the spinner over a list that already has content.
    const loadHistory = (quiet = false) => {
        if (!residentId || !facilityId) {
            setHistoryLoading(false);
            return;
        }
        const requestId = historyRequestRef.current + 1;
        historyRequestRef.current = requestId;
        const isCurrent = () => historyRequestRef.current === requestId;
        if (!quiet) setHistoryLoading(true);
        // Epoch milliseconds are the same instant in every time zone, so no facility time zone is needed here.
        const now = moment();
        // A day ahead of the device clock, see HISTORY_CLOCK_SKEW_MS.
        const toDate = now.valueOf() + HISTORY_CLOCK_SKEW_MS;
        const fromDate = now.clone().subtract(30, 'days').valueOf();
        // Greeting feed items are excluded from every other feed query (general
        // Social feed, group feeds) server-side, and only returned here because we
        // ask for them by explicit Category — see feed/list.js in live-backend.
        listFeed({
            facilityId: String(facilityId),
            callerResidentId: String(residentId),
            fromDate,
            toDate,
            Category: 'Greeting',
            page_no: 1,
            page_size: 20,
        })
            .then((res) => {
                if (!isCurrent()) return;
                const rows = toHistoryRows(res.feedItems || []);
                rows.sort((a, b) => historyTime(b.date) - historyTime(a.date));
                setHistory(rows);
                setHistoryLoadFailed(false);
            })
            .catch(() => {
                if (!isCurrent()) return;
                // Keep what is on screen. A quiet background refresh that fails stays silent.
                if (!quiet) setHistoryLoadFailed(true);
            })
            .finally(() => {
                if (isCurrent()) setHistoryLoading(false);
            });
    };

    const FULL_HISTORY_PAGE_SIZE = 20;
    // Used when "Oldest first" / "Most recipients" has to load every greeting before it can sort.
    const FULL_HISTORY_ALL_PAGE_SIZE = 100;

    const fetchFullHistoryPage = (pageNo: number, pageSize: number) =>
        listFeed({
            facilityId: String(facilityId),
            callerResidentId: String(residentId),
            fromDate: FULL_HISTORY_FROM,
            // A day ahead of the device clock, as in loadHistory.
            toDate: Date.now() + HISTORY_CLOCK_SKEW_MS,
            Category: 'Greeting',
            page_no: pageNo,
            page_size: pageSize,
        });

    // Separate from loadHistory: starts at FULL_HISTORY_FROM (before the product existed) with
    // "Load more" pagination, since the modal is meant to show every greeting ever sent rather
    // than the homepage preview's 30-day window.
    const loadFullHistory = (pageNo: number, append: boolean) => {
        if (!residentId || !facilityId) return;
        const requestId = fullHistoryRequestRef.current + 1;
        fullHistoryRequestRef.current = requestId;
        const isCurrent = () => fullHistoryRequestRef.current === requestId;
        setFullHistoryLoadingMore(append);
        setFullHistoryLoading(!append);
        fetchFullHistoryPage(pageNo, FULL_HISTORY_PAGE_SIZE)
            .then((res) => {
                if (!isCurrent()) return;
                const rows = toHistoryRows(res.feedItems || []);
                setFullHistoryRows((prev) => (append ? mergeHistoryRows(prev, rows) : rows));
                setFullHistoryTotalCount(typeof res.count === 'number' ? res.count : rows.length);
                setFullHistoryPage(pageNo);
                setFullHistoryLoadFailed(false);
            })
            .catch(() => {
                if (!isCurrent()) return;
                setFullHistoryLoadFailed(true);
            })
            .finally(() => {
                if (!isCurrent()) return;
                setFullHistoryLoading(false);
                setFullHistoryLoadingMore(false);
            });
    };

    const loadMoreFullHistory = () => loadFullHistory(fullHistoryPage + 1, true);

    // "Oldest first" and "Most recipients" only mean something over every greeting, so load them
    // all (in large pages) before sorting. Newest first stays paged.
    const loadAllFullHistory = async () => {
        if (!residentId || !facilityId) return;
        const requestId = fullHistoryRequestRef.current + 1;
        fullHistoryRequestRef.current = requestId;
        const isCurrent = () => fullHistoryRequestRef.current === requestId;
        setFullHistoryLoadingMore(false);
        setFullHistoryLoading(true);
        try {
            let all: HistoryRow[] = [];
            let total = 0;
            let pageNo = 1;
            let hasMore = true;
            while (hasMore) {
                // eslint-disable-next-line no-await-in-loop
                const res = await fetchFullHistoryPage(pageNo, FULL_HISTORY_ALL_PAGE_SIZE);
                if (!isCurrent()) return;
                const rows = toHistoryRows(res.feedItems || []);
                all = mergeHistoryRows(all, rows);
                total = typeof res.count === 'number' ? res.count : all.length;
                // An empty page ends the loop even if the total says more, so it can never spin.
                hasMore = rows.length > 0 && all.length < total;
                pageNo += 1;
            }
            setFullHistoryRows(all);
            setFullHistoryTotalCount(total);
            setFullHistoryPage(Math.max(1, Math.ceil(all.length / FULL_HISTORY_PAGE_SIZE)));
            setFullHistoryLoadFailed(false);
        } catch (_error) {
            if (isCurrent()) setFullHistoryLoadFailed(true);
        } finally {
            if (isCurrent()) setFullHistoryLoading(false);
        }
    };

    // ── Hello! / check-in (mirrors neighbors-home) ──
    const handleHelloClick = async () => {
        if (isFetchingStatus || isCheckInStatusLoading) return;
        if (checkInStatus) {
            sendToast('info', 'Already checked in for the day');
            return;
        }
        if (activeResident && activeResident._id) {
            setIsFetchingStatus(true);
            try {
                if (facilityTimeZone) {
                    const isCheckedIn = await fetchCheckInStatus(facilityTimeZone);
                    if (isCheckedIn) {
                        sendToast('info', 'Already checked in for the day');
                        setIsFetchingStatus(false);
                        return;
                    }
                }
            } catch {
                sendToast('error', 'Error checking check-in status');
                setIsFetchingStatus(false);
                return;
            }
            setIsFetchingStatus(false);
        }
        setShowCheckInConfirm(true);
    };

    const handleHelloCancel = () => {
        if (!isCheckInStatusLoading) setShowCheckInConfirm(false);
    };

    // What the Hello! card says after a check-in, from what the server said about the greeting.
    const buildHelloFeedback = (result: CheckInOutcomeResult): { tone: 'success' | 'warn'; text: React.ReactNode } => {
        // A cancellation waiting for period end switches Greetings off at once, and the server then answers
        // "not entitled": for this resident that means switched off, not never signed up.
        const outcome = result.outcome === 'not_signed_up' && greetingsCancelAtPeriodEnd ? 'switched_off' : result.outcome;
        switch (outcome) {
            case 'sent': {
                const count = result.recipientCount;
                return {
                    tone: 'success',
                    text:
                        typeof count === 'number' && count > 0
                            ? `Checked in, and your greeting was sent to ${count} contact${count > 1 ? 's' : ''}.`
                            : 'Checked in, and your greeting was sent.',
                };
            }
            case 'no_recipients':
                return {
                    tone: 'warn',
                    text: (
                        <span>
                            Checked in, but your greeting wasn't sent — you have no contacts set up to
                            receive greetings.{' '}
                            <a
                                href="#greetings-contacts"
                                onClick={(e) => {
                                    e.preventDefault();
                                    contactsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
                                }}
                            >
                                Choose who gets your messages
                            </a>
                            .
                        </span>
                    ),
                };
            case 'not_signed_up':
                return {
                    tone: 'warn',
                    text: "You're checked in to your community — but your family wasn't notified. Daily Greetings isn't turned on for your account yet.",
                };
            case 'switched_off':
                return {
                    tone: 'warn',
                    text: "You're checked in to your community — but your family wasn't notified. Daily Greetings is switched off for your account.",
                };
            case 'undeliverable':
                return {
                    tone: 'warn',
                    text: "Checked in, but your greeting wasn't sent. None of your contacts can receive it (opted out, or a number that can't get texts).",
                };
            case 'send_failed':
                return { tone: 'warn', text: "Checked in, but your greeting wasn't sent." };
            case 'moved_without_greeting':
                // The card was moved into checked_in through /checkin/update, which never sends a greeting.
                return {
                    tone: 'success',
                    text: greetingsAddonActive
                        ? "Checked in for the day. Your family wasn't messaged this time."
                        : 'Checked in for the day.',
                };
            default:
                // not_enabled / unknown: the check-in worked and the server said nothing about a greeting.
                return { tone: 'success', text: 'Checked in for the day.' };
        }
    };

    const handleCheckInConfirm = async () => {
        if (!activeResident || !activeResident._id) {
            sendToast('error', 'Selected resident data is missing or incomplete. Please contact support.');
            setShowCheckInConfirm(false);
            return;
        }
        if (checkInStatus) {
            // Already checked in (another tab or device got there first): nothing to send.
            sendToast('info', 'Already checked in for the day');
            setShowCheckInConfirm(false);
            return;
        }
        const payload: CheckInPayload = {
            column: 'checked_in',
            registrant: {
                _id: String(activeResident._id),
                FirstName: activeResident.FirstName || '',
                LastName: activeResident.LastName || '',
            },
            Facility: activeResident.Facility || '',
            registrantId: String(activeResident._id),
            roomName: activeResident.Unit?.Name || '',
            roomId: activeResident.Unit?.a4hRoomId || '',
            timestamp: Date.now(),
        };
        try {
            setIsCheckInStatusLoading(true);
            // Only /checkin/create triggers sendGreetingsToContacts server-side
            // (s2l checkinCreate -> /checkin/create). /checkin/update moves an
            // existing card into checked_in and does NOT send a greeting, so the
            // history refresh must be gated on this.
            let usedCreateRoute = false;
            // The server's authoritative answer about the greeting (greetingResult is forwarded
            // as-is by checkin/create). Stays null when no check-in happened.
            let result: CheckInOutcomeResult | null = null;
            if (!residentCheckInStatusObj) {
                const response = await CheckIn.createCheckInCard(payload);
                if (response.status === 'success') {
                    usedCreateRoute = true;
                    result = getCheckInOutcome('create', response);
                }
            } else {
                const updated: CheckInPayload = {
                    ...residentCheckInStatusObj,
                    column: 'checked_in',
                    prev_col: residentCheckInStatusObj.column,
                };
                const response = await CheckIn.updateCheckinCard(updated);
                if (response.status === 'success') {
                    setResidentCheckInStatusObj(updated);
                    setCheckInStatus(true);
                    result = getCheckInOutcome('update', response);
                }
            }
            if (!result) return;
            if (result.outcome === 'already_checked_in') {
                // A card for today already existed (or create moved it), so this press sent nothing.
                // Re-read the card before saying anything: only claim "already checked in" if it is.
                // Quiet: when this read fails the warning below is the one message the resident gets.
                const confirmed = facilityTimeZone ? await fetchCheckInStatus(facilityTimeZone, undefined, true) : false;
                if (confirmed) {
                    sendToast('info', 'Already checked in for the day');
                } else {
                    sendToast('warn', "We couldn't confirm your check-in. Please check your status and try again.");
                }
                return;
            }
            if (usedCreateRoute) setCheckInStatus(true);
            sendToast('success', 'Successfully checked in for the day');
            setHelloFeedback(buildHelloFeedback(result));
            if (usedCreateRoute) {
                // The greeting feed item is written server-side as part of the
                // check-in card write. After the immediate refresh below, retry
                // once more after a short delay so the just-sent greeting lands
                // in the history list even if the feed write settles a beat
                // behind the check-in response (otherwise the Hello! banner says
                // "sent" while history still reads "No greetings sent yet").
                if (historyRetryRef.current) window.clearTimeout(historyRetryRef.current);
                historyRetryRef.current = window.setTimeout(() => loadHistory(true), 3000);
                loadHistory();
            }
        } catch {
            sendToast('error', 'Error while updating the check in status');
        } finally {
            setIsCheckInStatusLoading(false);
            setShowCheckInConfirm(false);
        }
    };

    // ── My Greeting Message ──
    const startEditMessage = () => {
        setMessageDraft(greetingMessage);
        setIsEditingMessage(true);
    };

    const cancelEditMessage = () => setIsEditingMessage(false);

    const saveMessage = async () => {
        const trimmed = messageDraft.trim();
        if (!trimmed) {
            sendToast('warn', 'Please enter a greeting message.');
            return;
        }
        if (!residentId) return;
        try {
            setIsSavingMessage(true);
            // The backend merges SocialProfile key by key, so only the fields being changed are sent.
            // Do NOT pass greetingEnabled here: enrolment is owned by the subscription and sending it
            // would silently flip it.
            const socialProfile = { greetingMessage: trimmed, greetingType: 'custom' };
            const updated = await updateResident(residentId, { SocialProfile: socialProfile });
            if (!updated) throw new Error('Error updating greeting message');
            setGreetingMessage(trimmed);
            setIsEditingMessage(false);
            setUser({
                ...userObject,
                registrants: {
                    ...userObject.registrants,
                    list: userObject.registrants.list.map((resident: any) =>
                        resident._id === residentId
                            ? {
                                  ...resident,
                                  SocialProfile: {
                                      ...resident.SocialProfile,
                                      greetingMessage: trimmed,
                                      greetingType: 'custom',
                                  },
                              }
                            : resident,
                    ),
                },
            });
            sendToast('success', 'Greeting message saved');
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error updating greeting message');
        } finally {
            setIsSavingMessage(false);
        }
    };

    // ── Contacts ──
    const toggleContact = async (id: string) => {
        if (togglingId) return;
        const target = contacts.find((c) => c.id === id);
        if (!target || !residentId) return;
        const next = !target.greetingRecipient;
        setTogglingId(id);
        setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, greetingRecipient: next } : c)));
        try {
            // Only the flag is sent: the family backend no longer needs the contact's name for a flag-only update.
            await updateRelative(residentId, id, { greetingRecipient: next });
            forgetHomeGreetingsAnswer();
        } catch (error) {
            setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, greetingRecipient: !next } : c)));
            sendToast('error', error instanceof Error ? error.message : 'Could not update this contact');
        } finally {
            setTogglingId('');
        }
    };

    const visibleContacts = useMemo<GreetingContact[]>(() => {
        const query = contactSearch.trim().toLowerCase();
        let filtered = query ? contacts.filter((c) => c.name.toLowerCase().includes(query)) : [...contacts];
        if (receivingOnly) filtered = filtered.filter((c) => c.greetingRecipient);
        filtered.sort((a, b) => {
            if (contactSort === 'added') return b.seq - a.seq;
            if (contactSort === 'status') {
                if (a.greetingRecipient !== b.greetingRecipient) return a.greetingRecipient ? -1 : 1;
                return nameParts(a.name).first.localeCompare(nameParts(b.name).first);
            }
            const pa = nameParts(a.name);
            const pb = nameParts(b.name);
            const keyA = contactSort === 'last' ? pa.last || pa.first : pa.first;
            const keyB = contactSort === 'last' ? pb.last || pb.first : pb.first;
            return keyA.localeCompare(keyB);
        });
        return filtered;
    }, [contacts, contactSearch, contactSort, receivingOnly]);

    const updateScrollCue = () => {
        const el = contactsListRef.current;
        if (!el) {
            setShowScrollCue(false);
            return;
        }
        setShowScrollCue(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
    };

    // ── Invite ──
    const startInvite = () => {
        if (familyListLoadFailed) {
            sendToast('warn', 'Could not load invite availability. Please try again.');
            if (!contactsLoading) loadContacts();
            return;
        }
        if (availableInvites <= 0) {
            sendToast('warn', "You've used all your available invites.");
            return;
        }
        setInviteName('');
        setInvitePhone('');
        setInviteEmail('');
        setInviteGreetingOn(true);
        setInviteFormOpen(true);
    };

    const cancelInvite = () => setInviteFormOpen(false);

    // Backend /invites requires a non-empty first AND last name (matches the
    // family invite modal), so a single-word name keeps Send disabled.
    const inviteNameParts = nameParts(inviteName);
    const inviteValid =
        inviteNameParts.first.length > 0 &&
        inviteNameParts.last.length > 0 &&
        (invitePhone.trim().length > 0 || inviteEmail.trim().length > 0);

    const submitInvite = async () => {
        const name = inviteName.trim();
        const phoneRaw = invitePhone.trim();
        const emailRaw = inviteEmail.trim();
        if (!name || (!phoneRaw && !emailRaw) || !residentId) return;

        const e164 = phoneRaw ? toE164(phoneRaw) : '';
        if (phoneRaw && !e164) {
            sendToast('error', 'Please enter a valid phone number.');
            return;
        }
        const emailValid = !!emailRaw && isValidEmailAddress(emailRaw);
        if (emailRaw && !emailValid) {
            sendToast('error', 'Please enter a valid email address.');
            return;
        }
        // `contact` is the primary channel the invite is sent through.
        const contact = e164 || emailRaw;
        if (!contact) {
            sendToast('error', 'Enter a phone number or email to send an invite.');
            return;
        }
        const parts = nameParts(name);
        if (!parts.first || !parts.last) {
            sendToast('error', "Please enter the contact's first and last name.");
            return;
        }
        try {
            setIsSendingInvite(true);
            // The "Receive Greetings" toggle sets `greetingRecipient` on the invite,
            // which the family-backend carries onto the RegistrantRelatives row when
            // the invite is accepted — that flag is what greeting delivery filters
            // on. `announcementAlerts` is a separate concern (announcement SMS) and
            // is left at the backend default here.
            await sendInvite(
                residentId,
                contact,
                { firstName: parts.first, lastName: parts.last },
                DEFAULT_INVITE_MESSAGE,
                false,
                emailValid ? emailRaw : '',
                inviteGreetingOn,
            );
            sendToast('success', 'Invite sent');
            forgetHomeGreetingsAnswer();
            setInviteFormOpen(false);
            loadContacts();
        } catch (error) {
            const message = error && (error as any).message;
            sendToast('error', message ? message : 'Could not send the invite');
        } finally {
            setIsSendingInvite(false);
        }
    };

    // ── History ──
    const toggleHistoryRow = (key: string) =>
        setExpandedHistory((prev) => ({ ...prev, [key]: !prev[key] }));

    const fullHistorySorted = useMemo<HistoryRow[]>(() => {
        const rows = [...fullHistoryRows];
        rows.sort((a, b) => {
            if (fullHistorySort === 'oldest') return historyTime(a.date) - historyTime(b.date);
            if (fullHistorySort === 'most') return b.count - a.count;
            return historyTime(b.date) - historyTime(a.date);
        });
        return rows;
    }, [fullHistoryRows, fullHistorySort]);

    const openFullHistory = () => {
        setFullHistorySort('newest');
        setExpandedHistory({});
        setFullHistoryLoadFailed(false);
        setFullHistoryOpen(true);
        loadFullHistory(1, false);
    };

    const closeFullHistory = () => {
        // Drop any request still in flight so it cannot land in a closed window.
        fullHistoryRequestRef.current += 1;
        setFullHistoryLoading(false);
        setFullHistoryLoadingMore(false);
        setFullHistoryOpen(false);
    };

    const changeFullHistorySort = (sort: FullHistorySort) => {
        setFullHistorySort(sort);
        // Newest first is the paged view; the other two sorts need every greeting loaded first.
        if (sort !== 'newest' && fullHistoryRows.length < fullHistoryTotalCount) loadAllFullHistory();
    };

    // Keeps Tab inside the open window.
    const trapFullHistoryFocus = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Tab') return;
        const focusable = e.currentTarget.querySelectorAll<HTMLElement>(
            'button:not(:disabled), select:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled)',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    // Retry for a failed load: the first page, every page (Oldest / Most), or the next page.
    const retryFullHistory = () => {
        if (fullHistoryRows.length === 0) loadFullHistory(1, false);
        else if (fullHistorySort !== 'newest') loadAllFullHistory();
        else loadMoreFullHistory();
    };

    // ── Effects ──
    // Effects are keyed by the resident / facility id, not the whole profile object, because the object
    // is rebuilt on every profile refresh and would otherwise reload everything on the page.
    useEffect(() => {
        setGreetingMessage(activeResident?.SocialProfile?.greetingMessage || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeResident?._id, activeResident?.SocialProfile?.greetingMessage]);

    useEffect(() => {
        let cancelled = false;
        // Forget the previous facility's time zone until the new one arrives.
        setFacilityTimeZone('');
        (async () => {
            if (!facilityId) return;
            try {
                const facilityDetails = await fetchFacilityDetails(facilityId);
                if (!cancelled && facilityDetails.FacilityTimeZone) {
                    setFacilityTimeZone(facilityDetails.FacilityTimeZone);
                }
            } catch (error) {
                if (cancelled) return;
                sendToast(
                    'error',
                    error instanceof Error ? error.message : 'Error while fetching facility details',
                );
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [facilityId]);

    // Populate the check-in status line on load (neighbors-home only fetches on click).
    useEffect(() => {
        // Never show the previous resident's check-in while this one loads.
        setCheckInStatus(false);
        setResidentCheckInStatusObj(undefined);
        if (!facilityTimeZone || !residentId) return undefined;
        let stale = false;
        fetchCheckInStatus(facilityTimeZone, () => stale);
        return () => {
            stale = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityTimeZone, residentId, facilityId]);

    // Daily Greetings billing (Enh 4): fee/publishable key and the resident's saved cards come from
    // the same calls the (staff-side) membership billing widgets use. They are looked up apart from
    // the subscription, and each failure is kept apart: a fee failure leaves the sign-up showing
    // "Price unavailable", a card failure makes Sign Up ask to try again, and a subscription failure
    // shows the retry card -- none of them is read as "not signed up" or "no card".
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!residentId || !facilityId) {
                setGreetingsAddonLoading(false);
                return;
            }
            setGreetingsAddonLoading(true);
            setGreetingsLookupFailed(false);
            setCardsLookupFailed(false);
            const [configResult, methodsResult] = await settleAll<any>([
                fetchPaymentsPublicConfig(String(facilityId)),
                fetchResidentPaymentMethods(String(residentId), String(facilityId)),
            ]);
            if (cancelled) return;
            if (configResult.status === 'fulfilled') {
                setGreetingsPublicConfig(configResult.value as PaymentPublicConfig);
            } else {
                setGreetingsPublicConfig(null);
            }
            if (methodsResult.status === 'fulfilled') {
                const methods = (methodsResult.value || []) as ResidentPaymentMethod[];
                // The same rule the Order screen uses: a default card that has expired is no card.
                setHasDefaultPaymentMethod(resolveHasDefaultCard(methods));
            } else {
                setHasDefaultPaymentMethod(false);
                setCardsLookupFailed(true);
            }
            try {
                const sub = await getMembershipSubscription(String(residentId), String(facilityId), 'greetingsAddon');
                if (cancelled) return;
                if (isGreetingsAddonSubscription(sub)) {
                    setGreetingsSubscription(sub);
                } else {
                    // A different plan came back for the Greetings lookup: do not treat it as Greetings,
                    // and do not offer the sign-up card either.
                    setGreetingsSubscription(null);
                    setGreetingsLookupFailed(true);
                }
            } catch (error) {
                if (cancelled) return;
                setGreetingsSubscription(null);
                // "Subscription not found" is a real answer (never signed up); anything else
                // means we could not tell, so a paying resident is not shown the sign-up card.
                setGreetingsLookupFailed(!isMembershipSubscriptionNotFound(error));
            } finally {
                if (!cancelled) setGreetingsAddonLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [residentId, facilityId, greetingsReloadCount]);

    const handleGreetingsAddonChange = (subscription: MembershipSubscription | null) => {
        setGreetingsSubscription(subscription);
        forgetHomeGreetingsAnswer();
        if (subscription) setHasDefaultPaymentMethod(true);
    };

    // A card was just saved in the sign-up flow: remember it here, and let the order screens re-check their card eligibility.
    const handleGreetingsCardSaved = () => {
        setHasDefaultPaymentMethod(true);
        invalidateResidentOrderCardEligibility(residentId ? String(residentId) : undefined, facilityId ? String(facilityId) : undefined);
    };

    const retryGreetingsLookup = () => setGreetingsReloadCount((count) => count + 1);

    useEffect(() => {
        // A different resident: clear the previous one's contacts so a failed load cannot show them.
        setContacts([]);
        setInvitations([]);
        setAvailableInvites(0);
        setFamilyListLoadFailed(false);
        loadContacts();
        return () => {
            contactsRequestRef.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [residentId]);

    // The history window does not depend on the facility time zone (epoch milliseconds are the same
    // instant everywhere), so it loads once per resident instead of again when the time zone arrives.
    useEffect(() => {
        setHistory([]);
        setHistoryLoadFailed(false);
        setFullHistoryOpen(false);
        setFullHistoryRows([]);
        setFullHistoryTotalCount(0);
        setFullHistoryPage(1);
        setFullHistoryLoading(false);
        setFullHistoryLoadingMore(false);
        setFullHistoryLoadFailed(false);
        loadHistory();
        return () => {
            historyRequestRef.current += 1;
            fullHistoryRequestRef.current += 1;
            // A pending post-check-in refresh belongs to the resident that checked in.
            if (historyRetryRef.current) {
                window.clearTimeout(historyRetryRef.current);
                historyRetryRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [residentId, facilityId]);

    useEffect(() => {
        updateScrollCue();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleContacts, contactsLoading]);

    useEffect(() => {
        const onResize = () => updateScrollCue();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Reached via the Home screen's "Choose who gets your messages" link when
    // Hello! couldn't send a greeting for lack of contacts -- jump straight to
    // the contacts section instead of leaving the resident to find it.
    useEffect(() => {
        // greetingsAddonLoading gates the section below -- while it's true the
        // page renders a loader only, so contactsSectionRef isn't attached yet.
        // Re-run once loading resolves and the section actually mounts, instead
        // of only on first mount (an empty dep array would race the ref).
        if (greetingsAddonLoading) return;
        if ((location.state as { scrollTo?: string } | null)?.scrollTo === 'contacts') {
            contactsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [greetingsAddonLoading]);

    if (!activeResident) {
        return <Message content="Invalid user. Please contact support." />;
    }

    // Hello! confirm modal -- reuses the global check-in-confirm-* styles from neighbors-home.
    const helloConfirmModal = (
        <Modal
            open={showCheckInConfirm}
            onClose={handleHelloCancel}
            className="check-in-confirm-modal"
            closeOnDimmerClick={!isCheckInStatusLoading}
            closeOnEscape={!isCheckInStatusLoading}
        >
            <Modal.Content className="check-in-confirm-body">
                <div className="check-in-confirm-icon">
                    <svg
                        width="26"
                        height="26"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M9 11l3 3L22 4" />
                        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                    </svg>
                </div>
                <div className="check-in-confirm-title">Are you sure you want to check in for the day?</div>
            </Modal.Content>
            <Modal.Actions className="check-in-confirm-actions">
                <button
                    type="button"
                    className="check-in-confirm-btn check-in-confirm-btn-cancel"
                    disabled={isCheckInStatusLoading}
                    onClick={handleHelloCancel}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="check-in-confirm-btn check-in-confirm-btn-yes"
                    disabled={isCheckInStatusLoading}
                    onClick={handleCheckInConfirm}
                >
                    {isCheckInStatusLoading && <span className="check-in-confirm-spinner" />}
                    Yes
                </button>
            </Modal.Actions>
        </Modal>
    );

    // One press does two things: the check-in (free, always) and the greeting to family
    // (Daily Greetings). Spelling both out is what tells a resident that the greeting is
    // the optional paid part and the check-in is not.
    // `endingNote` is set while a cancellation is pending: the card shows the greeting as switched off.
    const renderHelloCard = (enrolled: boolean, endingNote?: string) => (
        <div className="hello-card">
            <button
                type="button"
                className="hello-btn"
                onClick={handleHelloClick}
                disabled={isFetchingStatus || isCheckInStatusLoading}
            >
                <span className="emoji" aria-hidden="true">
                    👋
                </span>
                Hello!
            </button>
            <div className="hello-status">
                {checkInStatus ? (
                    <>
                        <strong>Checked in</strong> — you're all set for today.
                    </>
                ) : (
                    "You haven't checked in yet today."
                )}
            </div>
            {helloFeedback && (
                <div className={`hello-feedback show ${helloFeedback.tone}`}>
                    {helloFeedback.tone === 'success' ? (
                        <Svg s={18} sw={2.5}>
                            <polyline points="20 6 9 17 4 12" />
                        </Svg>
                    ) : (
                        <Svg s={18} sw={2.3}>
                            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </Svg>
                    )}
                    <span>{helloFeedback.text}</span>
                </div>
            )}
            <div className="hello-does">
                <div className="hello-does-title">One press does two things</div>
                <div className="hello-does-row">
                    <div className="hello-does-text">
                        <div className="hello-does-main">Checks you in with your community</div>
                        <div className="hello-does-sub">Always on. Free, and it never turns off.</div>
                    </div>
                    <span className="hello-does-pill hello-does-pill--always">Always</span>
                </div>
                <div className={`hello-does-row${enrolled ? '' : ' is-off'}`}>
                    <div className="hello-does-text">
                        <div className="hello-does-main">Sends a greeting to your family</div>
                        <div className="hello-does-sub">
                            {enrolled
                                ? 'Goes to the contacts you choose below.'
                                : endingNote || 'Part of Daily Greetings — not turned on yet.'}
                        </div>
                    </div>
                    {enrolled ? (
                        <span className="hello-does-pill hello-does-pill--greetings">Greetings</span>
                    ) : endingNote ? null : (
                        <button
                            type="button"
                            className="hello-does-pill hello-does-pill--turn-on"
                            onClick={() => setSignupRequest((n) => n + 1)}
                        >
                            Turn on
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    if (greetingsAddonLoading || paymentAccessLoading) {
        return (
            <div className="greetings-page">
                <div className="greetings-content">
                    <Loader active inline="centered" style={{ marginTop: 40 }} />
                </div>
            </div>
        );
    }

    // Either lookup failing means we cannot tell what to show: a retry, never the sign-up card.
    // (A resident who already has Greetings keeps the full page when only the facility lookup failed.)
    if (greetingsLookupFailed || (facilityLookupFailed && !greetingsAddonActive)) {
        return (
            <div className="greetings-page">
                <div className="greetings-content">
                    <div className="page-title">Greetings</div>
                    <div className="page-sub">{PAGE_SUB}</div>
                    <div className="hello-card greetings-lookup-failed" role="alert">
                        <div className="greetings-lookup-failed-text">
                            We couldn&apos;t check your Greetings right now. Please try again.
                        </div>
                        <button
                            type="button"
                            className="greetings-lookup-failed-btn"
                            onClick={() => {
                                retryPaymentAccess();
                                setGreetingsReloadCount((count) => count + 1);
                            }}
                        >
                            Try again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // This community does not take card payments, so Daily Greetings is not sold here.
    if (!greetingsAddonActive && !facilityPaymentsEnabled) {
        return (
            <div className="greetings-page">
                <div className="greetings-content">
                    <div className="page-title">Greetings</div>
                    <div className="page-sub">{PAGE_SUB}</div>
                    <div className="hello-card greetings-lookup-failed" role="status">
                        <div className="greetings-lookup-failed-text">
                            Greetings isn&apos;t available for your community.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!greetingsAddonActive) {
        return (
            <div className="greetings-page">
                <div className="greetings-content">
                    <div className="page-title">Greetings</div>
                    <div className="page-sub">{PAGE_SUB}</div>
                    {renderHelloCard(false)}

                    <GreetingsAddonCard
                        mode="enroll"
                        residentId={String(residentId)}
                        facilityId={String(facilityId)}
                        subscription={greetingsSubscription}
                        feeCents={greetingsPublicConfig?.greetingsAddonAmountCents ?? null}
                        currency={greetingsPublicConfig?.greetingsAddonCurrency || 'USD'}
                        interval={greetingsPublicConfig?.greetingsAddonInterval || 'month'}
                        hasDefaultPaymentMethod={hasDefaultPaymentMethod}
                        stripePromise={greetingsStripePromise}
                        onChange={handleGreetingsAddonChange}
                        signupRequest={signupRequest}
                        onCardSaved={handleGreetingsCardSaved}
                        cardsLookupFailed={cardsLookupFailed}
                        onRetryLookup={retryGreetingsLookup}
                    />
                </div>
                {helloConfirmModal}
            </div>
        );
    }

    const recentHistory = history.slice(0, 3);

    const renderHistoryRow = (row: HistoryRow, keyPrefix: string) => {
        const key = `${keyPrefix}-${row.id}`;
        const zero = row.count === 0;
        const expanded = !!expandedHistory[key];
        const countText = zero
            ? 'Not sent — no recipients'
            : `Sent to ${row.count} contact${row.count > 1 ? 's' : ''}${row.failed > 0 ? `, ${row.failed} could not be reached` : ''}`;
        const unnamedRecipients = row.count - row.recipients.length;
        return (
            <div
                key={key}
                className={`history-item${expanded ? ' expanded' : ''}`}
                onClick={() => toggleHistoryRow(key)}
            >
                <div className="history-top">
                    <div className="history-meta">
                        <div className={`history-icon${zero ? ' zero' : ''}`}>
                            <Svg s={14} sw={2.3}>
                                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                            </Svg>
                        </div>
                        <div className="history-text">
                            <div className="history-count">{countText}</div>
                            <div className="history-date">{formatHistoryDate(row.date, facilityTimeZone)}</div>
                        </div>
                    </div>
                    <Svg s={16} sw={2.4} className="history-chevron">
                        <polyline points="9 18 15 12 9 6" />
                    </Svg>
                </div>
                <div className="history-detail">
                    {row.message ? <div className="history-message">&ldquo;{row.message}&rdquo;</div> : null}
                    {(row.recipients.length > 0 || unnamedRecipients > 0) && (
                        <div className="history-recipients">
                            {row.recipients.map((n, i) => (
                                <span className="history-recipient-chip" key={`${key}-r${i}`}>
                                    {n}
                                </span>
                            ))}
                            {unnamedRecipients > 0 && (
                                <span className="history-recipient-chip history-recipient-chip--unnamed">
                                    +{unnamedRecipients} more (no name on file)
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="greetings-page">
            <div className="greetings-content">
                <div className="page-title">Greetings</div>
                <div className="page-sub">{PAGE_SUB}</div>

                <GreetingsAddonCard
                    mode="manage"
                    manageSection="status"
                    residentId={String(residentId)}
                    facilityId={String(facilityId)}
                    subscription={greetingsSubscription}
                    feeCents={greetingsPublicConfig?.greetingsAddonAmountCents ?? null}
                    currency={greetingsPublicConfig?.greetingsAddonCurrency || 'USD'}
                    interval={greetingsPublicConfig?.greetingsAddonInterval || 'month'}
                    hasDefaultPaymentMethod={hasDefaultPaymentMethod}
                    stripePromise={greetingsStripePromise}
                    onChange={handleGreetingsAddonChange}
                    onRetryLookup={retryGreetingsLookup}
                />

                {renderHelloCard(
                    !greetingsCancelAtPeriodEnd,
                    greetingsCancelAtPeriodEnd
                        ? `Switched off. Greetings ends ${
                              greetingsEndLabel ? `on ${greetingsEndLabel}` : 'at the end of this billing period'
                          }. Keep it active under View My Subscription below.`
                        : undefined,
                )}

                {/* ── My Greeting Message ── */}
                <div className="section-card">
                    <div className="section-title">
                        <Svg s={17} sw={2}>
                            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                        </Svg>
                        My Greeting Message
                    </div>
                    <div className="section-desc">
                        This is what's sent to your contacts when you press Hello!
                    </div>
                    {isEditingMessage ? (
                        <div>
                            <textarea
                                className="message-textarea"
                                value={messageDraft}
                                onChange={(e) => setMessageDraft(e.target.value)}
                                maxLength={MESSAGE_MAX_LENGTH}
                                placeholder="Write the message your family sees when you check in…"
                            />
                            <div
                                className={`message-counter${
                                    messageDraft.length >= MESSAGE_MAX_LENGTH ? ' at-limit' : ''
                                }`}
                            >
                                {messageDraft.length}/{MESSAGE_MAX_LENGTH}
                            </div>
                            <div className="message-save-row">
                                <button
                                    type="button"
                                    className="message-cancel-btn"
                                    onClick={cancelEditMessage}
                                    disabled={isSavingMessage}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="message-save-btn"
                                    onClick={saveMessage}
                                    disabled={isSavingMessage || messageDraft.length > MESSAGE_MAX_LENGTH}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className={`message-display${greetingMessage ? '' : ' is-empty'}`}>
                                {/* Preview fills the tokens the way live-be does when it sends (first match of each). */}
                                {(greetingMessage &&
                                    greetingMessage
                                        .replace('{residentName}', getResidentDisplayFirstName(activeResident))
                                        .replace('{contactName}', 'Name')) ||
                                    `No custom message yet. Your family gets: Hi [name], I just wanted to say hello today - ${
                                        getResidentDisplayFirstName(activeResident) || 'your name'
                                    }.`}
                            </div>
                            {allowEdit && (
                                <div className="message-edit-row">
                                    <button type="button" className="message-edit-btn" onClick={startEditMessage}>
                                        Edit Message
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── My Greeting Contacts ── */}
                <div className="section-card" id="greetings-contacts" ref={contactsSectionRef}>
                    <div className="section-title">
                        <Svg s={17} sw={2}>
                            <path d="M17 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 00-3-3.87" />
                            <path d="M16 3.13a4 4 0 010 7.75" />
                        </Svg>
                        My Greeting Contacts
                    </div>
                    <div className="section-desc">
                        Choose who receives your greeting. Invite a new contact, or ask staff to add one directly.
                    </div>

                    <div className="contact-search-row">
                        <div className="contact-search-box">
                            <Svg s={15} sw={2.2}>
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </Svg>
                            <input
                                type="text"
                                placeholder="Search contacts..."
                                value={contactSearch}
                                onChange={(e) => setContactSearch(e.target.value)}
                            />
                        </div>
                        <div className="contact-sort-box">
                            <Svg s={14} sw={2.2}>
                                <path d="M3 6h18M6 12h12M10 18h4" />
                            </Svg>
                            <select
                                value={contactSort}
                                onChange={(e) => setContactSort(e.target.value as ContactSort)}
                                aria-label="Sort contacts"
                            >
                                <option value="first">First Name</option>
                                <option value="last">Last Name</option>
                                <option value="added">Recently Added</option>
                                <option value="status">Greeting Status</option>
                            </select>
                        </div>
                        <button
                            type="button"
                            className={`contact-filter-chip${receivingOnly ? ' active' : ''}`}
                            onClick={() => setReceivingOnly((v) => !v)}
                        >
                            <span className="contact-filter-check">
                                <Svg s={10} sw={3.2}>
                                    <polyline points="20 6 9 17 4 12" />
                                </Svg>
                            </span>
                            Receiving only
                        </button>
                    </div>

                    <div className="contacts-scroll-wrap">
                        <div className="contacts-list" ref={contactsListRef} onScroll={updateScrollCue}>
                            {contactsLoading ? (
                                <div className="contacts-loading">
                                    <Loader active inline size="small" />
                                </div>
                            ) : contacts.length === 0 ? (
                                <div className="contacts-empty">
                                    No contacts added yet. Ask your community staff to add your family contacts so
                                    they can receive your greetings.
                                </div>
                            ) : visibleContacts.length === 0 ? (
                                <div className="contacts-no-match">
                                    {receivingOnly && !contactSearch.trim()
                                        ? 'No contacts are currently set to receive greetings.'
                                        : `No contacts match "${contactSearch.trim()}".`}
                                </div>
                            ) : (
                                visibleContacts.map((c) => (
                                    <div className="contact-row" key={c.id}>
                                        <div className="contact-avatar">{initials(c.name)}</div>
                                        <div className="contact-info">
                                            <div className="contact-name">{c.name}</div>
                                            <div className="contact-relationship">{c.relationship}</div>
                                        </div>
                                        <div className="contact-toggle-wrap">
                                            <div className="contact-toggle-label">
                                                {c.greetingRecipient ? 'Receiving' : 'Off'}
                                            </div>
                                            <button
                                                type="button"
                                                className={`toggle-switch${c.greetingRecipient ? ' on' : ''}`}
                                                onClick={() => toggleContact(c.id)}
                                                disabled={togglingId === c.id}
                                                aria-label={`Toggle greetings for ${c.name}`}
                                            >
                                                <span className="toggle-knob" />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        {showScrollCue && (
                            <div className="contacts-scroll-cue">
                                <Svg s={20} sw={2.5}>
                                    <polyline points="6 9 12 15 18 9" />
                                </Svg>
                            </div>
                        )}
                    </div>

                    {inviteFormOpen ? (
                        <div className="invite-form">
                            <div className="invite-field">
                                <label htmlFor="greetings-invite-name">Contact's Full Name</label>
                                <input
                                    id="greetings-invite-name"
                                    type="text"
                                    placeholder="e.g. Susan Reyes"
                                    value={inviteName}
                                    onChange={(e) => setInviteName(e.target.value)}
                                />
                            </div>
                            <div className="invite-field">
                                <label htmlFor="greetings-invite-phone">Phone Number</label>
                                <input
                                    id="greetings-invite-phone"
                                    type="tel"
                                    placeholder="e.g. (555) 123-4567"
                                    value={invitePhone}
                                    onChange={(e) => setInvitePhone(e.target.value)}
                                />
                            </div>
                            <div className="invite-field">
                                <label htmlFor="greetings-invite-email">Email Address</label>
                                <input
                                    id="greetings-invite-email"
                                    type="email"
                                    placeholder="e.g. susan@email.com"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                />
                            </div>
                            <div className="invite-toggle-row">
                                <div>
                                    <label>Receive Greetings</label>
                                    <div className="invite-toggle-sub">Send this contact your Hello! greeting</div>
                                </div>
                                <button
                                    type="button"
                                    className={`toggle-switch${inviteGreetingOn ? ' on' : ''}`}
                                    onClick={() => setInviteGreetingOn((v) => !v)}
                                    aria-label="Toggle receive greetings for this invite"
                                >
                                    <span className="toggle-knob" />
                                </button>
                            </div>
                            <div className="invite-note" style={{ marginTop: 14 }}>
                                We'll send them an invite to confirm their contact info.
                            </div>
                            <div className="invite-form-actions">
                                <button
                                    type="button"
                                    className="invite-cancel-btn"
                                    onClick={cancelInvite}
                                    disabled={isSendingInvite}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="invite-send-btn"
                                    onClick={submitInvite}
                                    disabled={!inviteValid || isSendingInvite}
                                >
                                    Send Invite
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="invite-row">
                            <button type="button" className="invite-btn" onClick={startInvite}>
                                <Svg s={15} sw={2.3}>
                                    <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                                    <circle cx="8.5" cy="7" r="4" />
                                    <line x1="20" y1="8" x2="20" y2="14" />
                                    <line x1="17" y1="11" x2="23" y2="11" />
                                </Svg>
                                Invite a Contact
                            </button>
                        </div>
                    )}

                    {invitations.length > 0 && (
                        <div className="invited-list">
                            <div className="invited-label">Invited</div>
                            {invitations.map((invite) => (
                                <div className="invited-row" key={invite._id}>
                                    <div className="invited-name">{invite.Contact}</div>
                                    <div className="invited-status">
                                        {invite.LastInviteSent
                                            ? `Invited ${new Date(invite.LastInviteSent).toLocaleDateString('en-US', {
                                                  month: 'short',
                                                  day: 'numeric',
                                              })}`
                                            : 'Invite sent'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Greeting History ── */}
                <div className="section-card">
                    <div className="history-header-row">
                        <div className="section-title" style={{ marginBottom: 0 }}>
                            <Svg s={17} sw={2}>
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </Svg>
                            Greeting History
                        </div>
                        <button
                            type="button"
                            className="history-viewall-btn"
                            onClick={openFullHistory}
                            ref={historyViewAllRef}
                            aria-label="View all greeting history"
                            title="View all greeting history"
                        >
                            <Svg s={17} sw={2}>
                                <line x1="8" y1="6" x2="21" y2="6" />
                                <line x1="8" y1="12" x2="21" y2="12" />
                                <line x1="8" y1="18" x2="21" y2="18" />
                                <line x1="3" y1="6" x2="3.01" y2="6" />
                                <line x1="3" y1="12" x2="3.01" y2="12" />
                                <line x1="3" y1="18" x2="3.01" y2="18" />
                            </Svg>
                        </button>
                    </div>
                    <div className="section-desc" style={{ marginTop: 4 }}>
                        Your 3 most recent greetings.
                    </div>
                    <div className="history-list">
                        {historyLoading ? (
                            <div className="history-loading">
                                <Loader active inline size="small" />
                            </div>
                        ) : recentHistory.length === 0 && !historyLoadFailed ? (
                            <div className="history-empty">
                                No greetings sent yet. Press Hello! to let your contacts know you're up and about.
                            </div>
                        ) : (
                            <>
                                {recentHistory.map((row) => renderHistoryRow(row, 'main'))}
                                {historyLoadFailed && (
                                    <div className="history-error" role="alert">
                                        <span>We couldn&apos;t load your greetings.</span>
                                        <button type="button" className="history-retry-btn" onClick={() => loadHistory()}>
                                            Try again
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* ── Subscription (View My Subscription → cancel) ── */}
                <GreetingsAddonCard
                    mode="manage"
                    manageSection="action"
                    residentId={String(residentId)}
                    facilityId={String(facilityId)}
                    subscription={greetingsSubscription}
                    feeCents={greetingsPublicConfig?.greetingsAddonAmountCents ?? null}
                    currency={greetingsPublicConfig?.greetingsAddonCurrency || 'USD'}
                    interval={greetingsPublicConfig?.greetingsAddonInterval || 'month'}
                    hasDefaultPaymentMethod={hasDefaultPaymentMethod}
                    stripePromise={greetingsStripePromise}
                    onChange={handleGreetingsAddonChange}
                    onRetryLookup={retryGreetingsLookup}
                />
            </div>

            {helloConfirmModal}

            {/* Full greeting-history window. The Semantic Modal only gives Escape and click-outside to close;
                the dialog role and aria props, moving focus in on mount and back on unmount, and the Tab
                trap are added here by hand. It renders outside .greetings-page, so the content carries that
                class again to keep the history row styles. */}
            <Modal
                open={fullHistoryOpen}
                onClose={closeFullHistory}
                onMount={() => fullHistoryCloseRef.current?.focus()}
                onUnmount={() => historyViewAllRef.current?.focus()}
                className="greetings-fullhistory-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="greetings-fullhistory-title"
            >
                <div className="greetings-page greetings-page--in-modal" onKeyDown={trapFullHistoryFocus}>
                    <div className="fullhistory-modal">
                        <div className="fullhistory-header">
                            <div className="fullhistory-title" id="greetings-fullhistory-title">
                                All Greeting History
                            </div>
                            <button
                                type="button"
                                className="fullhistory-close"
                                ref={fullHistoryCloseRef}
                                onClick={closeFullHistory}
                                aria-label="Close"
                            >
                                <Svg s={16} sw={2.4}>
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </Svg>
                            </button>
                        </div>
                        <div className="fullhistory-controls">
                            <label htmlFor="greetings-fullhistory-sort">Sort by</label>
                            <select
                                id="greetings-fullhistory-sort"
                                className="fullhistory-sort"
                                value={fullHistorySort}
                                disabled={fullHistoryLoading || fullHistoryLoadingMore}
                                onChange={(e) => changeFullHistorySort(e.target.value as FullHistorySort)}
                            >
                                <option value="newest">Newest first</option>
                                <option value="oldest">Oldest first</option>
                                <option value="most">Most recipients</option>
                            </select>
                        </div>
                        <div className="fullhistory-body">
                            {fullHistoryLoading ? (
                                <Loader active inline="centered" size="small" />
                            ) : (
                                <>
                                    {fullHistorySorted.length === 0 && !fullHistoryLoadFailed && (
                                        <div className="history-empty">No greetings sent yet.</div>
                                    )}
                                    {fullHistorySorted.map((row) => renderHistoryRow(row, 'full'))}
                                    {fullHistoryLoadFailed ? (
                                        <div className="history-error" role="alert">
                                            <span>We couldn&apos;t load your greetings.</span>
                                            <button
                                                type="button"
                                                className="history-retry-btn"
                                                onClick={retryFullHistory}
                                            >
                                                Try again
                                            </button>
                                        </div>
                                    ) : (
                                        fullHistoryRows.length < fullHistoryTotalCount && (
                                            <button
                                                type="button"
                                                className="fullhistory-load-more"
                                                disabled={fullHistoryLoadingMore}
                                                onClick={loadMoreFullHistory}
                                            >
                                                {fullHistoryLoadingMore ? (
                                                    <Loader active inline size="tiny" />
                                                ) : (
                                                    'Load more'
                                                )}
                                            </button>
                                        )
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default NeighborsGreetings;
